import { setPathValue, valuesEqual, walkPath } from './binding';
import { isA2UIEnvelopeMessage } from './canvasPayload';
import type { A2UIEnvelopeMessage, A2UISurfaceSnapshot, RecordAny, SurfaceComponent } from './types';

export function extractA2UIEnvelopeList(payload?: RecordAny, messages?: RecordAny[]): A2UIEnvelopeMessage[] {
  const source =
    Array.isArray(messages) && messages.length > 0
      ? messages
      : Array.isArray(payload?.objects) && payload.objects.length > 0
        ? (payload.objects as RecordAny[])
        : Array.isArray(payload?.messages) && payload.messages.length > 0
          ? (payload.messages as RecordAny[])
          : [];
  return source.filter(isA2UIEnvelopeMessage);
}

export function parseDataModelUpdates(envelopes: A2UIEnvelopeMessage[]): RecordAny {
  let result: RecordAny = {};
  for (const envelope of envelopes) {
    const update = envelope.dataModelUpdate;
    if (!update) continue;
    const contents = Array.isArray(update.contents) ? (update.contents as RecordAny[]) : [];
    // Honor `path` so updates scoped to a sub-tree (e.g. path="/foo") write into result.foo.<key> instead of polluting the root with same-named keys.
    const basePath = typeof update.path === 'string' ? update.path : '/';
    for (const content of contents) {
      const key = typeof content.key === 'string' ? content.key : '';
      if (!key) continue;
      const parsed = readDataEntryValue(content);
      if (parsed === undefined) continue;
      result = setPathValue(result, `${basePath}/${key}`, parsed);
    }
  }
  return result;
}

function readDataEntryValue(entry: RecordAny): unknown {
  if (typeof entry.valueString === 'string') return entry.valueString;
  if (typeof entry.valueNumber === 'number') return entry.valueNumber;
  if (typeof entry.valueBool === 'boolean') return entry.valueBool;
  if (typeof entry.valueBoolean === 'boolean') return entry.valueBoolean;
  // Use isArray (not length) so `valueArray: []` / `valueMap: []` round-trip as "empty collection" — distinguishes "user cleared selection" from "never initialized".
  if (Array.isArray(entry.valueArray)) {
    return (entry.valueArray as unknown[]).map((item) => (item && typeof item === 'object' ? readDataEntryValue(item as RecordAny) : item)).filter((item) => item !== undefined);
  }
  if (Array.isArray(entry.valueMap)) {
    const nested: RecordAny = {};
    for (const item of entry.valueMap as RecordAny[]) {
      const itemKey = typeof item.key === 'string' ? item.key : '';
      if (!itemKey) continue;
      const parsed = readDataEntryValue(item);
      if (parsed !== undefined) nested[itemKey] = parsed;
    }
    return nested;
  }
  return undefined;
}

export function parseSurfaceComponents(envelopes: A2UIEnvelopeMessage[]): { root: string; components: SurfaceComponent[] } {
  const begin = envelopes.toReversed().find((item) => item.beginRendering);
  const root = typeof begin?.beginRendering?.root === 'string' ? begin.beginRendering.root : 'root';
  const componentMap = new Map<string, SurfaceComponent>();
  for (const envelope of envelopes) {
    const components = Array.isArray(envelope.surfaceUpdate?.components) ? (envelope.surfaceUpdate.components as SurfaceComponent[]) : [];
    for (const component of components) {
      if (typeof component?.id !== 'string' || !component.id) continue;
      componentMap.set(component.id, component);
    }
  }
  return { root, components: Array.from(componentMap.values()) };
}

export function createA2UISurfaceSnapshot(payload?: RecordAny, messages?: RecordAny[]): A2UISurfaceSnapshot {
  const envelopes = extractA2UIEnvelopeList(payload, messages);
  const surface = parseSurfaceComponents(envelopes);
  return { ...surface, data: parseDataModelUpdates(envelopes), envelopes };
}

function resolveSupportWidgetPath(expr: unknown): string | undefined {
  if (!expr || typeof expr !== 'object' || typeof (expr as RecordAny).path !== 'string') return undefined;
  const path = ((expr as RecordAny).path as string).trim();
  return path || undefined;
}

function resolveSupportWidgetBoolean(expr: unknown, data: RecordAny): boolean {
  if (!expr || typeof expr !== 'object') return false;
  const record = expr as RecordAny;
  if (typeof record.path === 'string') {
    const current = walkPath(record.path, data);
    if (typeof current === 'boolean') return current;
  }
  if (typeof record.literalBool === 'boolean') return record.literalBool;
  if (typeof record.literalBoolean === 'boolean') return record.literalBoolean;
  return false;
}

export function getBooleanAnyOfDerivedUpdates(components: SurfaceComponent[], data: RecordAny): Array<{ path: string; value: boolean }> {
  const updates: Array<{ path: string; value: boolean }> = [];
  for (const component of components) {
    if (!component?.component || typeof component.component !== 'object') continue;
    const entry = Object.entries(component.component)[0];
    if (!entry || entry[0] !== 'BooleanAnyOf') continue;
    const config = (entry[1] || {}) as RecordAny;
    const outputPath = resolveSupportWidgetPath(config.output);
    if (!outputPath) continue;
    const inputs = (Array.isArray(config.inputs) ? config.inputs : []).filter((input): input is RecordAny => Boolean(input && typeof input === 'object' && resolveSupportWidgetPath(input) !== outputPath));
    updates.push({ path: outputPath, value: inputs.some((input) => resolveSupportWidgetBoolean(input, data)) });
  }
  return updates;
}

export function applyBooleanAnyOfDerivedUpdates(components: SurfaceComponent[], data: RecordAny): RecordAny {
  let next = data;
  for (const update of getBooleanAnyOfDerivedUpdates(components, data)) {
    if (valuesEqual(walkPath(update.path, next), update.value)) continue;
    next = setPathValue(next, update.path, update.value);
  }
  return next;
}
